/**
 * Login Page
 * Displays Spotify login button and handles auto-redirect
 */

import { useEffect, useState } from 'react';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthProvider';
import { SpotifyLogin } from '../features/auth/components/SpotifyLogin';
import { useAuthStore } from '../stores/auth';

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const { clearTokens } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [showResetButton, setShowResetButton] = useState(false);

  // Demo mode: ?demo shows hidden test links
  const isDemoMode = searchParams.has('demo');

  // Show reset button after 3 seconds (for stuck sessions)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowResetButton(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Redirect if already authenticated
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  const handleResetSession = () => {
    clearTokens();
    setShowResetButton(false);
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-6xl font-bold text-white mb-2">Splister</h1>
          <p className="text-gray-400 text-lg">
            Smart Playlist Management for Spotify
          </p>
        </div>

        {/* Login Button */}
        <div className="bg-gray-800 rounded-lg p-8 shadow-2xl">
          <p className="text-gray-300 mb-6">
            Connect your Spotify account to get started
          </p>
          <div className="flex justify-center">
            <SpotifyLogin />
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 text-gray-400 text-sm space-y-2">
          <p>Advanced playlist reordering</p>
          <p>Audio feature analysis</p>
          <p>Sequence builder</p>
        </div>

        {/* Reset Session Button (appears after 3s) */}
        {showResetButton && (
          <div className="mt-6">
            <button
              onClick={handleResetSession}
              className="text-xs text-gray-500 hover:text-gray-300 underline transition-colors"
            >
              Having trouble? Reset session
            </button>
          </div>
        )}

        {/* Demo Mode: Test error pages (?demo in URL) */}
        {isDemoMode && (
          <div className="mt-8 p-4 bg-purple-900/30 border border-purple-500/30 rounded-lg">
            <p className="text-purple-300 text-xs font-semibold mb-3 uppercase tracking-wide">
              Demo Mode
            </p>
            <div className="flex flex-col gap-2">
              <Link
                to="/spotify-error?error=access_denied"
                className="text-sm text-purple-300 hover:text-purple-100 underline"
              >
                Test: Spotify access_denied error
              </Link>
              <Link
                to="/invitation-required"
                className="text-sm text-purple-300 hover:text-purple-100 underline"
              >
                Test: Invitation required (no user data)
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
